/**
 * Opening (door/window) annotation properties — registration and getters.
 *
 * An OPENING is a wall opening rendered like an AREA polygon, but logically
 * tied to a skeleton edge. Properties registered here:
 * - opening_kind (SELECT, PROJECT_GLOBAL) — DOOR | WINDOW (subtype)
 * - opening_skeleton_edge_id (STRING, PROJECT_GLOBAL) — referenced SkeletonEdgeData.id (nullable)
 * - opening_room_ids (JSON, PROJECT_GLOBAL) — 0–2 ROOM annotation identifiers this opening connects
 *
 * Edge ids are globally unique UUIDs, so they alone identify which skeleton
 * the opening belongs to — no separate `opening_skeleton_id` is needed.
 *
 * Non-hook version for Potree. React hook wrappers (if needed) live alongside
 * MeasureProperties.ts in the client.
 */

import { AnnotationType, PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { TypedJsonProperty } from '../../reactive/TypedJsonProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';
import { OpeningKind, DoorType } from 'shared-types';

export function registerOpeningProperties() {
  if (!propertyRegistry.isRegistered('opening_kind')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'opening_kind',
      name: 'type',
      valueType: PropertyValueType.SELECT,
      defaultValue: OpeningKind.DOOR,
      annotationTypes: [AnnotationType.OPENING],
      description: 'Subtype of the opening: door or window',
      ui: { group: 'information', order: 1, hiddenInGroup: true },
      validation: {
        options: [
          { value: OpeningKind.DOOR, label: 'door' },
          { value: OpeningKind.WINDOW, label: 'window' },
        ],
      },
    });
  }

  // `opening_skeleton_edge_id` is an internal linkage property — set by
  // tools, consumed by the orphan-on-delete pass in PointoramaWallSketch.setData.
  // `ui.hidden` keeps it reactive + persisted but excluded from the
  // auto-generated property panel.
  if (!propertyRegistry.isRegistered('opening_skeleton_edge_id')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'opening_skeleton_edge_id',
      name: 'skeleton edge',
      valueType: PropertyValueType.STRING,
      defaultValue: '',
      annotationTypes: [AnnotationType.OPENING],
      description: 'Identifier (SkeletonEdgeData.id) of the referenced edge, or empty if not linked',
      ui: { hidden: true },
    });
  }

  // `opening_room_ids` links an opening to the 1 or 2 ROOM annotations it
  // connects (a door between two rooms, a window into one). Stored as a
  // JSON-serialized string[] of annotation identifiers; access via
  // `getOpeningRoomIdsProperty` for typed reads/writes.
  if (!propertyRegistry.isRegistered('opening_room_ids')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'opening_room_ids',
      name: 'rooms',
      valueType: PropertyValueType.JSON,
      defaultValue: '[]',
      annotationTypes: [AnnotationType.OPENING],
      description: 'Identifiers of the 1 or 2 ROOM annotations this opening connects',
      ui: { hidden: true },
    });
  }

  // Door swing orientation — two booleans driving the 2D floor-plan door glyph
  // (leaf + swing arc). Only meaningful for DOOR openings; windows ignore them.
  //   opening_hinge_at_start: hinge at the start (−) end of the opening's
  //     along-wall axis when true, else the end (+).
  //   opening_swing_positive: door swings toward +wallNorm when true, else
  //     −wallNorm.
  // Until `opening_swing_initialized` is true, the renderer derives sensible
  // initial values from the opening's rooms and persists them; after that the
  // user's choices stick.
  if (!propertyRegistry.isRegistered('opening_hinge_at_start')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'opening_hinge_at_start',
      name: 'hinge at start',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: true,
      annotationTypes: [AnnotationType.OPENING],
      description: 'Door hinge at the start end of the opening (else the far end)',
      ui: {
        group: 'information',
        order: 2,
        hiddenInGroup: true,
        // Swing/hinge only matter for doors with a leaf — hide for windows and
        // the leaf-less open doorway.
        visibilityConditions: [
          { propertyIdentifier: 'opening_kind', value: OpeningKind.DOOR },
          { propertyIdentifier: 'opening_door_type', value: DoorType.OPEN_DOORWAY, negate: true },
        ],
      },
    });
  }

  if (!propertyRegistry.isRegistered('opening_swing_positive')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'opening_swing_positive',
      // Kind-neutral: for a door it's the swing side, for a rollable window it's
      // the side the shutter box sits on.
      name: 'side',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: true,
      annotationTypes: [AnnotationType.OPENING],
      description: 'Which side of the wall the opening symbol faces (door swing / window shutter side)',
      ui: {
        group: 'information',
        order: 3,
        hiddenInGroup: true,
        // Relevant for any opening with an oriented symbol — i.e. everything
        // except the leaf-less open doorway.
        visibilityConditions: [{ propertyIdentifier: 'opening_door_type', value: DoorType.OPEN_DOORWAY, negate: true }],
      },
    });
  }

  // Internal: whether the swing booleans have been auto-initialized from room
  // geometry yet. Hidden from the panel.
  if (!propertyRegistry.isRegistered('opening_swing_initialized')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'opening_swing_initialized',
      name: 'swing initialized',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.OPENING],
      description: 'Internal: swing booleans have been seeded from room geometry',
      ui: { hidden: true },
    });
  }

  // Door subtype — drives the 2D floor-plan glyph. Only affects DOOR openings
  // (windows ignore it). Mirrors the standard architectural door symbols.
  if (!propertyRegistry.isRegistered('opening_door_type')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'opening_door_type',
      name: 'door type',
      valueType: PropertyValueType.SELECT,
      defaultValue: DoorType.SINGLE,
      annotationTypes: [AnnotationType.OPENING],
      description: 'Door subtype, drawn with the matching floor-plan glyph',
      ui: {
        group: 'information',
        order: 4,
        hiddenInGroup: true,
        visibilityConditions: [{ propertyIdentifier: 'opening_kind', value: OpeningKind.DOOR }],
      },
      validation: {
        options: [
          { value: DoorType.SINGLE, label: 'single' },
          { value: DoorType.DOUBLE, label: 'double' },
          { value: DoorType.BIFOLD, label: 'bifold' },
          { value: DoorType.SLIDING, label: 'sliding' },
          { value: DoorType.BYPASS_SLIDING, label: 'bypass sliding' },
          { value: DoorType.POCKET, label: 'pocket' },
          { value: DoorType.SURFACE_SLIDING, label: 'surface sliding' },
          { value: DoorType.CLOSET, label: 'closet' },
          { value: DoorType.OPEN_DOORWAY, label: 'open doorway' },
        ],
      },
    });
  }

  // Rollable / roller-shutter window. Only affects WINDOW openings.
  if (!propertyRegistry.isRegistered('opening_rollable')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'opening_rollable',
      name: 'rollable',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: false,
      annotationTypes: [AnnotationType.OPENING],
      description: 'Render this window as a rollable (roller-shutter) window',
      ui: {
        group: 'information',
        order: 5,
        hiddenInGroup: true,
        visibilityConditions: [{ propertyIdentifier: 'opening_kind', value: OpeningKind.WINDOW }],
      },
    });
  }
}

registerOpeningProperties();

export function getOpeningKindProperty(openingId: string): ReactiveProperty<OpeningKind> {
  const service = getGlobalPropertyService();
  return service.getProperty('opening_kind', openingId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<OpeningKind>;
}

export function getOpeningWallSketchEdgeIdProperty(openingId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('opening_skeleton_edge_id', openingId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

export function getOpeningHingeAtStartProperty(openingId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('opening_hinge_at_start', openingId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<boolean>;
}

export function getOpeningSwingPositiveProperty(openingId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('opening_swing_positive', openingId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<boolean>;
}

export function getOpeningSwingInitializedProperty(openingId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('opening_swing_initialized', openingId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<boolean>;
}

export function getOpeningDoorTypeProperty(openingId: string): ReactiveProperty<DoorType> {
  const service = getGlobalPropertyService();
  return service.getProperty('opening_door_type', openingId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<DoorType>;
}

export function getOpeningRollableProperty(openingId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('opening_rollable', openingId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<boolean>;
}

const openingRoomIdsCache = new Map<string, TypedJsonProperty<string[]>>();

export function getOpeningRoomIdsProperty(openingId: string): TypedJsonProperty<string[]> {
  const cached = openingRoomIdsCache.get(openingId);
  if (cached && !cached.isDestroyed) return cached;

  const service = getGlobalPropertyService();
  const raw = service.getProperty('opening_room_ids', openingId, PropertyType.PROJECT_GLOBAL);
  const typed = new TypedJsonProperty<string[]>(raw);
  openingRoomIdsCache.set(openingId, typed);
  return typed;
}
